/**
 * Geocoding pipeline for bulk seizure-case uploads.
 *
 * Strategy (per unique location+country pair):
 *   1. If lat/lng already present → source = "provided", skip API
 *   2. Mapbox geocoding with full location string + country bias
 *      → relevance ≥ 0.5 → source = "api_precise"
 *   3. Mapbox geocoding with simplified location string
 *      (strips admin noise words, tries last comma-segment)
 *      → any result → source = "api_approximate"
 *   4. Country centroid fallback → source = "country_centroid"
 *
 * De-duplicates by "location::country" key so each unique pair
 * hits the API only once, regardless of how many rows share it.
 *
 * Country coverage: a curated ISO/centroid table handles common
 * source/transit/destination countries without an API round trip.
 * Any country outside that table still resolves correctly — the
 * Mapbox search runs unbiased (no incorrect country filter) and the
 * centroid falls back to a live Mapbox country-level lookup instead
 * of silently defaulting to Kenya.
 */

const MAPBOX_TOKEN =
  "pk.eyJ1Ijoia2FidWltdXJpaXRoaSIsImEiOiJjbWJ0Zm5hY3UwM2I1MmtzMGR2aW10NTl5In0.GVgsbN2VdblT43_9zpGJuw";

// ── Country name aliases → canonical name used for all lookups ────────
const COUNTRY_ALIASES: Record<string, string> = {
  "england":                "United Kingdom",
  "scotland":                "United Kingdom",
  "wales":                   "United Kingdom",
  "northern ireland":        "United Kingdom",
  "great britain":           "United Kingdom",
  "uk":                      "United Kingdom",
  "drc":                     "Democratic Republic of the Congo",
  "dr congo":                "Democratic Republic of the Congo",
  "congo-kinshasa":          "Democratic Republic of the Congo",
  "congo":                   "Republic of the Congo",
  "congo-brazzaville":       "Republic of the Congo",
  "ivory coast":             "Ivory Coast",
  "cote d'ivoire":           "Ivory Coast",
  "côte d'ivoire":           "Ivory Coast",
  "usa":                     "United States",
  "us":                      "United States",
  "united states of america":"United States",
  "uae":                     "United Arab Emirates",
  "hong kong":               "Hong Kong",
};

function canonicalCountry(country: string): string {
  const raw = (country ?? "").trim();
  if (!raw) return "Kenya";
  return COUNTRY_ALIASES[raw.toLowerCase()] ?? raw;
}

// ── Country → ISO 3166-1 alpha-2 (Mapbox country bias) ────────────────
const COUNTRY_ISO: Record<string, string> = {
  // East / Southern / Central Africa
  Kenya:                                 "ke",
  Tanzania:                              "tz",
  Uganda:                                "ug",
  Ethiopia:                              "et",
  Rwanda:                                "rw",
  Somalia:                               "so",
  Mozambique:                            "mz",
  Zambia:                                "zm",
  Zimbabwe:                              "zw",
  "Democratic Republic of the Congo":    "cd",
  "Republic of the Congo":               "cg",
  Malawi:                                "mw",
  "South Africa":                        "za",
  Botswana:                              "bw",
  Namibia:                               "na",
  Angola:                                "ao",
  Sudan:                                 "sd",
  "South Sudan":                         "ss",
  Chad:                                  "td",
  "Central African Republic":            "cf",
  Gabon:                                 "ga",
  "Equatorial Guinea":                   "gq",
  Burundi:                               "bi",
  Djibouti:                              "dj",
  Eritrea:                               "er",
  Lesotho:                               "ls",
  Eswatini:                              "sz",
  Cameroon:                              "cm",
  // West Africa
  Nigeria:                               "ng",
  Ghana:                                 "gh",
  Benin:                                 "bj",
  Togo:                                  "tg",
  Senegal:                               "sn",
  "Ivory Coast":                         "ci",
  Gambia:                                "gm",
  Guinea:                                "gn",
  "Guinea-Bissau":                       "gw",
  Liberia:                               "lr",
  "Sierra Leone":                        "sl",
  Mali:                                  "ml",
  Niger:                                 "ne",
  "Burkina Faso":                        "bf",
  Mauritania:                            "mr",
  // North Africa
  Egypt:                                 "eg",
  Morocco:                               "ma",
  Algeria:                               "dz",
  Tunisia:                               "tn",
  Libya:                                 "ly",
  // Indian Ocean
  Madagascar:                            "mg",
  Comoros:                               "km",
  Seychelles:                            "sc",
  Mauritius:                             "mu",
  // Europe (common trafficking destination/transit countries)
  "United Kingdom":                      "gb",
  France:                                "fr",
  Italy:                                 "it",
  Spain:                                 "es",
  Germany:                               "de",
  Belgium:                               "be",
  Netherlands:                           "nl",
  Austria:                               "at",
  Portugal:                              "pt",
  Switzerland:                           "ch",
  Sweden:                                "se",
  Norway:                                "no",
  Denmark:                               "dk",
  Poland:                                "pl",
  "Czech Republic":                      "cz",
  Ireland:                               "ie",
  Greece:                                "gr",
  Russia:                                "ru",
  // Asia / Middle East
  China:                                 "cn",
  "Hong Kong":                           "hk",
  Vietnam:                               "vn",
  Thailand:                              "th",
  Indonesia:                             "id",
  Malaysia:                              "my",
  Singapore:                             "sg",
  Philippines:                           "ph",
  India:                                 "in",
  Japan:                                 "jp",
  "South Korea":                         "kr",
  "United Arab Emirates":                "ae",
  Qatar:                                 "qa",
  "Saudi Arabia":                        "sa",
  Pakistan:                              "pk",
  Bangladesh:                            "bd",
  // Americas / Oceania
  "United States":                       "us",
  Canada:                                "ca",
  Brazil:                                "br",
  Mexico:                                "mx",
  Australia:                             "au",
  "New Zealand":                         "nz",
};

// ── Country centroid coordinates (fast path — no API call needed) ─────
const COUNTRY_CENTROIDS: Record<string, { lat: number; lng: number }> = {
  Kenya:                                 { lat: -0.0236,  lng: 37.9062  },
  Tanzania:                              { lat: -6.3690,  lng: 34.8888  },
  Uganda:                                { lat: 1.3733,   lng: 32.2903  },
  Ethiopia:                              { lat: 9.1450,   lng: 40.4897  },
  Rwanda:                                { lat: -1.9403,  lng: 29.8739  },
  Somalia:                               { lat: 5.1521,   lng: 46.1996  },
  Mozambique:                            { lat: -18.6657, lng: 35.5296  },
  Zambia:                                { lat: -13.1339, lng: 27.8493  },
  Zimbabwe:                              { lat: -19.0154, lng: 29.1549  },
  "Democratic Republic of the Congo":    { lat: -4.0383,  lng: 21.7587  },
  "Republic of the Congo":               { lat: -0.2280,  lng: 15.8277  },
  Malawi:                                { lat: -13.2543, lng: 34.3015  },
  "South Africa":                        { lat: -30.5595, lng: 22.9375  },
  Botswana:                              { lat: -22.3285, lng: 24.6849  },
  Namibia:                               { lat: -22.9576, lng: 18.4904  },
  Angola:                                { lat: -11.2027, lng: 17.8739  },
  Sudan:                                 { lat: 12.8628,  lng: 30.2176  },
  "South Sudan":                         { lat: 6.8770,   lng: 31.3070  },
  Chad:                                  { lat: 15.4542,  lng: 18.7322  },
  "Central African Republic":            { lat: 6.6111,   lng: 20.9394  },
  Gabon:                                 { lat: -0.8037,  lng: 11.6094  },
  "Equatorial Guinea":                   { lat: 1.6508,   lng: 10.2679  },
  Burundi:                               { lat: -3.3731,  lng: 29.9189  },
  Djibouti:                              { lat: 11.8251,  lng: 42.5903  },
  Eritrea:                               { lat: 15.1794,  lng: 39.7823  },
  Lesotho:                               { lat: -29.6100, lng: 28.2336  },
  Eswatini:                              { lat: -26.5225, lng: 31.4659  },
  Cameroon:                              { lat: 7.3697,   lng: 12.3547  },
  Nigeria:                               { lat: 9.0820,   lng: 8.6753   },
  Ghana:                                 { lat: 7.9465,   lng: -1.0232  },
  Benin:                                 { lat: 9.3077,   lng: 2.3158   },
  Togo:                                  { lat: 8.6195,   lng: 0.8248   },
  Senegal:                               { lat: 14.4974,  lng: -14.4524 },
  "Ivory Coast":                         { lat: 7.5400,   lng: -5.5471  },
  Gambia:                                { lat: 13.4432,  lng: -15.3101 },
  Guinea:                                { lat: 9.9456,   lng: -9.6966  },
  "Guinea-Bissau":                       { lat: 11.8037,  lng: -15.1804 },
  Liberia:                               { lat: 6.4281,   lng: -9.4295  },
  "Sierra Leone":                        { lat: 8.4606,   lng: -11.7799 },
  Mali:                                  { lat: 17.5707,  lng: -3.9962  },
  Niger:                                 { lat: 17.6078,  lng: 8.0817   },
  "Burkina Faso":                        { lat: 12.2383,  lng: -1.5616  },
  Mauritania:                            { lat: 21.0079,  lng: -10.9408 },
  Egypt:                                 { lat: 26.8206,  lng: 30.8025  },
  Morocco:                               { lat: 31.7917,  lng: -7.0926  },
  Algeria:                               { lat: 28.0339,  lng: 1.6596   },
  Tunisia:                               { lat: 33.8869,  lng: 9.5375   },
  Libya:                                 { lat: 26.3351,  lng: 17.2283  },
  Madagascar:                            { lat: -18.7669, lng: 46.8691  },
  Comoros:                               { lat: -11.6455, lng: 43.3333  },
  Seychelles:                            { lat: -4.6796,  lng: 55.4920  },
  Mauritius:                             { lat: -20.3484, lng: 57.5522  },
  "United Kingdom":                      { lat: 54.0000,  lng: -2.0000  },
  France:                                { lat: 46.6034,  lng: 2.2137   },
  Italy:                                 { lat: 42.5000,  lng: 12.5000  },
  Spain:                                 { lat: 40.0000,  lng: -3.7000  },
  Germany:                               { lat: 51.1657,  lng: 10.4515  },
  Belgium:                               { lat: 50.5039,  lng: 4.4699   },
  Netherlands:                           { lat: 52.1326,  lng: 5.2913   },
  Austria:                               { lat: 47.5162,  lng: 14.5501  },
  Portugal:                              { lat: 39.3999,  lng: -8.2245  },
  Switzerland:                           { lat: 46.8182,  lng: 8.2275   },
  Sweden:                                { lat: 62.0000,  lng: 15.0000  },
  Norway:                                { lat: 64.5731,  lng: 11.5281  },
  Denmark:                               { lat: 56.2639,  lng: 9.5018   },
  Poland:                                { lat: 51.9194,  lng: 19.1451  },
  "Czech Republic":                      { lat: 49.8175,  lng: 15.4730  },
  Ireland:                               { lat: 53.4129,  lng: -8.2439  },
  Greece:                                { lat: 39.0742,  lng: 22.0000  },
  Russia:                                { lat: 55.7558,  lng: 37.6173  },
  China:                                 { lat: 35.0000,  lng: 103.8000 },
  "Hong Kong":                           { lat: 22.3193,  lng: 114.1694 },
  Vietnam:                               { lat: 16.0000,  lng: 108.0000 },
  Thailand:                              { lat: 15.8700,  lng: 100.9925 },
  Indonesia:                             { lat: -2.5000,  lng: 118.0000 },
  Malaysia:                              { lat: 4.2105,   lng: 108.0000 },
  Singapore:                             { lat: 1.3521,   lng: 103.8198 },
  Philippines:                           { lat: 12.8797,  lng: 121.7740 },
  India:                                 { lat: 22.0000,  lng: 79.0000  },
  Japan:                                 { lat: 36.2048,  lng: 138.2529 },
  "South Korea":                         { lat: 35.9078,  lng: 127.7669 },
  "United Arab Emirates":                { lat: 24.0000,  lng: 54.0000  },
  Qatar:                                 { lat: 25.3548,  lng: 51.1839  },
  "Saudi Arabia":                        { lat: 24.0000,  lng: 45.0000  },
  Pakistan:                              { lat: 30.3753,  lng: 69.3451  },
  Bangladesh:                            { lat: 23.6850,  lng: 90.3563  },
  "United States":                       { lat: 39.8283,  lng: -98.5795 },
  Canada:                                { lat: 56.1304,  lng: -106.3468},
  Brazil:                                { lat: -10.3333, lng: -53.2000 },
  Mexico:                                { lat: 23.6345,  lng: -102.5528},
  Australia:                             { lat: -25.2744, lng: 133.7751 },
  "New Zealand":                         { lat: -41.5000, lng: 172.8344 },
};

const DEFAULT_CENTROID = { lat: -0.0236, lng: 37.9062 }; // last-resort fallback (Kenya)

// ── Noise words common in East-African location strings ───────────────
const NOISE_WORDS = new Set([
  "area", "sub-county", "sub county", "subcounty", "location",
  "ward", "village", "within", "along", "near", "route", "road",
  "farm", "forest", "ranch", "reserve", "township", "market",
  "hotel", "shop", "estate", "district", "region", "county",
  "division", "sector", "block", "junction", "border", "port",
]);

// ── Types ──────────────────────────────────────────────────────────────
export interface GeocodingStats {
  uniqueTotal:  number;  // distinct location+country pairs to process
  uniqueDone:   number;  // how many unique pairs resolved so far
  provided:     number;  // already had lat/lng in the source data
  precise:      number;  // api_precise
  approximate:  number;  // api_approximate
  centroid:     number;  // country_centroid
}

export type GeocodingProgressFn = (stats: GeocodingStats) => void;

// ── Internal helpers ───────────────────────────────────────────────────

function cleanToken(token: string): string {
  return token
    .split(/\s+/)
    .filter(w => !NOISE_WORDS.has(w.toLowerCase()))
    .join(" ")
    .replace(/[,.-]+$/, "")
    .trim();
}

function simplifyLocation(location: string): string | null {
  const parts = location
    .split(",")
    .map(p => p.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    const countryNames = new Set(Object.keys(COUNTRY_ISO).map(c => c.toLowerCase()));
    for (let i = parts.length - 1; i >= 0; i--) {
      if (!countryNames.has(parts[i].toLowerCase())) {
        const cleaned = cleanToken(parts[i]);
        if (cleaned.length > 2) return cleaned;
      }
    }
  }

  const cleaned = cleanToken(parts[0] ?? location);
  return cleaned !== location.trim() && cleaned.length > 2 ? cleaned : null;
}

async function callMapbox(
  query: string,
  countryCode: string | undefined
): Promise<{ lat: number; lng: number; relevance: number } | null> {
  try {
    const types = "country,region,district,locality,place,neighborhood,address,poi";
    let url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
      `?access_token=${MAPBOX_TOKEN}&limit=1&types=${types}`;
    // Only bias to a specific country when we actually know its ISO code.
    // Forcing an unrelated code (e.g. defaulting to "ke") for unmapped
    // countries would make Mapbox filter out the correct result entirely.
    if (countryCode) url += `&country=${countryCode}`;

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    if (!data.features?.length) return null;

    const [lng, lat] = data.features[0].center as [number, number];
    const relevance: number = data.features[0].relevance ?? 0;
    return { lat, lng, relevance };
  } catch {
    return null;
  }
}

// ── Dynamic country-centroid fallback ──────────────────────────────────
// Covers any country outside the curated COUNTRY_CENTROIDS table above by
// asking Mapbox for the country's own centroid, cached per country name
// so it's only ever looked up once regardless of how many rows share it.
const countryCentroidCache = new Map<string, { lat: number; lng: number }>();

async function resolveCountryCentroid(country: string): Promise<{ lat: number; lng: number }> {
  const staticHit = COUNTRY_CENTROIDS[country];
  if (staticHit) return staticHit;

  const cached = countryCentroidCache.get(country);
  if (cached) return cached;

  let resolved = DEFAULT_CENTROID;
  try {
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(country)}.json` +
      `?access_token=${MAPBOX_TOKEN}&limit=1&types=country`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      const feature = data.features?.[0];
      if (feature) {
        const [lng, lat] = feature.center as [number, number];
        resolved = { lat, lng };
      }
    }
  } catch {
    // keep DEFAULT_CENTROID
  }

  countryCentroidCache.set(country, resolved);
  return resolved;
}

async function geocodeSingle(
  location: string | null | undefined,
  countryRaw: string
): Promise<{ lat: number; lng: number; geocodeSource: string }> {
  const country = canonicalCountry(countryRaw);
  const iso     = COUNTRY_ISO[country]; // undefined for uncovered countries — callMapbox omits the bias

  if (!location || location === "N/A") {
    const centroid = await resolveCountryCentroid(country);
    return { ...centroid, geocodeSource: "country_centroid" };
  }

  // Step 1 — full string
  const r1 = await callMapbox(`${location}, ${country}`, iso);
  if (r1 && r1.relevance >= 0.5) {
    return { lat: r1.lat, lng: r1.lng, geocodeSource: "api_precise" };
  }

  // Step 2 — simplified string
  const simple = simplifyLocation(location);
  if (simple) {
    const r2 = await callMapbox(`${simple}, ${country}`, iso);
    if (r2) {
      return { lat: r2.lat, lng: r2.lng, geocodeSource: "api_approximate" };
    }
  }

  // Step 3 — country centroid
  const centroid = await resolveCountryCentroid(country);
  return { ...centroid, geocodeSource: "country_centroid" };
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Geocode an array of seizure-case records in-place.
 *
 * - Records that already have lat AND lng get geocodeSource = "provided".
 * - All other records are geocoded via the 3-step cascade.
 * - De-duplicates by location+country (canonicalized, e.g. "DRC" and
 *   "Democratic Republic of the Congo" collapse to the same pair) so
 *   each unique pair hits the Mapbox API only once.
 * - Calls onProgress after each unique pair is resolved.
 */
export async function geocodeRecords(
  records: any[],
  onProgress?: GeocodingProgressFn
): Promise<any[]> {

  // ── 1. Count records that already have coordinates ─────────────────
  let providedCount = 0;
  records.forEach(r => {
    const lat = parseFloat(r.latitude);
    const lng = parseFloat(r.longitude);
    if (!isNaN(lat) && !isNaN(lng)) providedCount++;
  });

  // ── 2. Build unique pairs that need geocoding ──────────────────────
  const uniqueKeys = new Set<string>();
  records.forEach(r => {
    const lat = parseFloat(r.latitude);
    const lng = parseFloat(r.longitude);
    if (isNaN(lat) || isNaN(lng)) {
      uniqueKeys.add(`${r.location ?? ""}::${canonicalCountry(r.country)}`);
    }
  });

  const uniqueArr = Array.from(uniqueKeys);
  const cache = new Map<string, { lat: number; lng: number; geocodeSource: string }>();

  const stats: GeocodingStats = {
    uniqueTotal:  uniqueArr.length,
    uniqueDone:   0,
    provided:     providedCount,
    precise:      0,
    approximate:  0,
    centroid:     0,
  };

  onProgress?.(stats);

  // ── 3. Geocode each unique pair sequentially ───────────────────────
  for (const key of uniqueArr) {
    const [location, country] = key.split("::");
    const result = await geocodeSingle(location, country);
    cache.set(key, result);

    stats.uniqueDone++;
    if (result.geocodeSource === "api_precise")          stats.precise++;
    else if (result.geocodeSource === "api_approximate") stats.approximate++;
    else                                                 stats.centroid++;

    onProgress?.(stats);

    // 120 ms breathing room — stays well under Mapbox's 600 req/min free-tier limit
    await new Promise(res => setTimeout(res, 120));
  }

  // ── 4. Fan results back to every row ──────────────────────────────
  return records.map(r => {
    const lat = parseFloat(r.latitude);
    const lng = parseFloat(r.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { ...r, geocodeSource: "provided" };
    }

    const key    = `${r.location ?? ""}::${canonicalCountry(r.country)}`;
    const result = cache.get(key);
    if (!result) return r;

    return {
      ...r,
      latitude:      result.lat,
      longitude:     result.lng,
      geocodeSource: result.geocodeSource,
    };
  });
}
